import type { TFunction } from 'i18next';
import * as Yup from 'yup';
import type {
  CreateLeadPayload,
  Lead,
  LeadTreatmentInterest,
  UpdateLeadPayload,
} from '@/modules/lead/api/lead.api';
import type { ReferredByPickerValue } from '@/modules/referrer/types';
import {
  buildReferredByWritePayload,
  summaryToPickerValue,
} from '@/modules/referrer/utils/referredByPicker';
import {
  formatPhoneForInput,
  optionalEmailValidation,
  phoneValidation,
} from '@/shared/utils/validation';

/** Khớp maxlength `note` trên model Lead. */
export const LEAD_INITIAL_NOTE_MAX_LENGTH = 2000;

export interface LeadFormValues {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  gender: '' | 'male' | 'female' | 'other';
  clinicId: string;
  customerSource: string;
  referredBy: ReferredByPickerValue | null;
  treatmentInterest: '' | LeadTreatmentInterest;
  assigneeId: string;
  note: string;
}

export const createEmptyLeadForm = (): LeadFormValues => ({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  gender: '',
  clinicId: '',
  customerSource: '',
  referredBy: null,
  treatmentInterest: '',
  assigneeId: '',
  note: '',
});

/** Location state when navigating to Add Lead with draft values (e.g. quick-create). */
export type AddLeadPrefill = {
  firstName?: string;
  phone?: string;
  clinicId?: string;
  customerSource?: string;
};

export type AddLeadLocationState = {
  /** Open create dialog on `/leads` (legacy `/leads/add` redirect). */
  openAddLead?: boolean;
  prefill?: AddLeadPrefill;
};

export const leadToFormValues = (lead: Lead): LeadFormValues => ({
  firstName: lead.firstName ?? '',
  lastName: lead.lastName ?? '',
  phone: formatPhoneForInput(lead.phone),
  email: lead.email ?? '',
  gender: lead.gender ?? '',
  clinicId: lead.clinicId ?? '',
  customerSource: lead.customerSource?.id ?? '',
  referredBy: lead.referredBy?.id ? summaryToPickerValue(lead.referredBy) : null,
  treatmentInterest: lead.treatmentInterest ?? '',
  assigneeId: lead.assigneeId ?? '',
  note: lead.note ?? '',
});

const buildReferredBy = (form: LeadFormValues) =>
  buildReferredByWritePayload(form.referredBy) ?? undefined;

export const buildCreateLeadPayload = (form: LeadFormValues): CreateLeadPayload => ({
  firstName: form.firstName.trim(),
  lastName: form.lastName.trim() || undefined,
  phone: form.phone.trim() || undefined,
  email: form.email.trim() || undefined,
  gender: form.gender || undefined,
  clinicId: form.clinicId || undefined,
  customerSource: form.customerSource,
  referredBy: buildReferredBy(form),
  treatmentInterest: form.treatmentInterest || undefined,
  assigneeId: form.assigneeId || undefined,
  note: form.note.trim() || undefined,
});

type LeadTranslate = TFunction<['lead', 'common']>;

/** Kiểm tra khi bấm Lưu. Ô có `maxLength` tự chặn quá ký tự — schema không báo lỗi độ dài. */
export const buildLeadValidationSchema = (t: LeadTranslate) =>
  Yup.object({
    firstName: Yup.string().trim().required(t('lead:validation.firstNameRequired')),
    lastName: Yup.string().trim(),
    phone: phoneValidation,
    email: optionalEmailValidation,
    gender: Yup.string(),
    clinicId: Yup.string(),
    customerSource: Yup.string().trim().required(t('lead:validation.customerSourceRequired')),
    referredBy: Yup.mixed().nullable(),
    treatmentInterest: Yup.string(),
    assigneeId: Yup.string(),
    note: Yup.string(),
  });

export const buildUpdateLeadPayload = (form: LeadFormValues): UpdateLeadPayload => ({
  firstName: form.firstName.trim(),
  lastName: form.lastName.trim() || undefined,
  phone: form.phone.trim() || undefined,
  email: form.email.trim() || undefined,
  gender: form.gender || null,
  clinicId: form.clinicId || null,
  customerSource: form.customerSource,
  referredBy: buildReferredByWritePayload(form.referredBy),
  treatmentInterest: form.treatmentInterest || null,
  assigneeId: form.assigneeId || null,
  note: form.note.trim() || null,
});
